// web/src/components/Contact.tsx
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import PageLayout from "./PageLayout";
import { Account } from "./TopMenuBar";
import { useViewport } from "../hooks/useViewport";
import { ANDROID_TABLET_MENU_OFFSET, ANDROID_TABLET_SCROLL_REDUCTION } from "../lib/androidLayout";

const API_BASE = (import.meta as any).env?.VITE_API_BASE || "http://localhost:8100";
const LEADERBOARD_BG = "LeaderboardBackground.png";

function getAssetPath(path: string): string {
  const cleanPath = path.replace(/^\//, "");
  const isBrowser = typeof window !== "undefined";

  const cap = isBrowser ? (window as any).Capacitor : null;
  const isNative =
    Boolean(cap) &&
    (typeof cap.isNativePlatform === "function"
      ? cap.isNativePlatform()
      : typeof cap.getPlatform === "function"
        ? cap.getPlatform() === "ios" || cap.getPlatform() === "android"
        : cap.platform === "ios" || cap.platform === "android");

  if (isNative) {
    try {
      const baseUrl = new URL(window.location.href);
      return baseUrl.origin + "/" + cleanPath;
    } catch (e) {
      console.warn("[getAssetPath] Failed to construct URL, using fallback:", e);
      return "/" + cleanPath;
    }
  }

  return "./" + cleanPath;
}

// Card component (moved outside to prevent re-renders)
function Card(props: { title?: string; children: React.ReactNode; footer?: React.ReactNode; isMobileLandscape?: boolean; iosPadHeaderFontSize?: string; iosPadSmallFontSize?: string }) {
  const { isMobileLandscape = false } = props;
  return (
    <div className={isMobileLandscape ? "w-full rounded-xl shadow-xl border-4" : "mx-auto max-w-xl rounded-2xl shadow-xl border-4"} style={{
      background: 'linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)',
      borderColor: '#8b6542'
    }}>
      <div className={isMobileLandscape ? "p-3" : "p-6"}>
        {props.title && <h2 className={isMobileLandscape ? "text-sm font-bold mb-1.5" : "text-2xl font-bold mb-4"} style={{ 
          color: '#3e2615',
          fontSize: props.iosPadHeaderFontSize
        }}>{props.title}</h2>}
        <div className={isMobileLandscape ? "space-y-1" : "space-y-3"}>{props.children}</div>
        {props.footer && <div className={isMobileLandscape ? "mt-2 border-t pt-1.5 text-xs" : "mt-6 border-t pt-4 text-sm"} style={{ 
          borderColor: '#8b6542', 
          color: '#3e2615',
          fontSize: props.iosPadSmallFontSize
        }}>{props.footer}</div>}
      </div>
    </div>
  );
}

interface ContactProps {
  account?: Account | null;
  onLogout?: () => void;
}

export default function Contact({ account, onLogout }: ContactProps) {
  const navigate = useNavigate();
  const viewport = useViewport({ mobileBreakpoint: 1024 });
  const isMobileLandscape = !viewport.isPortrait && viewport.isMobile;
  const isAndroid = typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform() === 'android';
  const isIOSApp = typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform && (window as any).Capacitor.getPlatform() === 'ios';
  
  // Bulletproof iPhone detection - exclude iPad (must be after viewport declaration)
  const isIPhone = (() => {
    if (!isIOSApp) return false;
    const userAgent = typeof navigator !== 'undefined' ? (navigator.userAgent || navigator.vendor || '').toLowerCase() : '';
    const platform = typeof navigator !== 'undefined' ? (navigator as any).platform || '' : '';
    const maxTouchPoints = typeof navigator !== 'undefined' ? (navigator as any).maxTouchPoints || 0 : 0;
    const maxDim = Math.max(viewport.width, viewport.height);
    const minDim = Math.min(viewport.width, viewport.height);
    
    // Explicit iPad in user agent
    if (userAgent.includes('ipad')) return false;
    
    // Modern iPadOS 13+ reports as MacIntel with touch support
    if (platform.toLowerCase() === 'macintel' && maxTouchPoints > 1 && maxDim >= 768 && minDim >= 768) return false;
    
    // If dimensions suggest iPad, exclude it
    if (maxDim >= 768 && minDim >= 768) return false;
    
    // Explicit iPhone in user agent (but not iPad)
    if (userAgent.includes('iphone') && !userAgent.includes('ipad')) return true;
    
    // iPod touch counts as iPhone-like
    if (userAgent.includes('ipod')) return true;
    
    // If it's iOS app and not iPad (by dimensions), assume iPhone
    if (isIOSApp && (maxDim < 768 || minDim < 768)) return true;
    
    return false;
  })();
  
  // Bulletproof iPad detection - exclude iPhone (must be after isIPhone declaration)
  const isIOSPad = (() => {
    if (!isIOSApp) return false;
    if (isIPhone) return false; // If it's iPhone, it's not iPad
    return Math.max(viewport.width, viewport.height) >= 768;
  })();
  const isIOSLarge = isIOSApp && !isIPhone && viewport.width >= 768; // Exclude iPhone
  const isAndroidTablet = viewport.isAndroidTablet;
  const androidInsetLeft = isAndroid ? Math.round(viewport.width * (70 / 1920)) : 0;
  const androidInsetRight = isAndroid ? Math.round(viewport.width * (112 / 1920)) : 0;
  const androidMenuOffset = isAndroidTablet ? ANDROID_TABLET_MENU_OFFSET : 0;
  const scrollReduction = isAndroidTablet ? ANDROID_TABLET_SCROLL_REDUCTION : 0;
  // Keep contact form text closer to default to avoid oversized inputs
  const textScale = isAndroidTablet ? 3.6 : (isIOSApp ? 1.12 : 1); // bump overall tablet text
  const androidContentShift = isAndroidTablet ? 12 : 0;
  const androidBodyFontSize = isAndroidTablet ? '18px' : undefined;
  const androidBodyLineHeight = isAndroidTablet ? 1.3 : undefined;
  const iosPadGutterX = isIOSPad ? Math.round(viewport.width * 0.05) : 0;
  const iosPadGutterY = isIOSPad ? Math.round(viewport.height * 0.05) : 0;
  const iosPadShiftX = isIOSPad ? 10 : 0;
  const iosPadShiftY = isIOSPad ? 24 : 0;
  
  // Font sizes for iOS iPad
  const iosPadHeaderFontSize = isIOSPad && isMobileLandscape ? '20px' : undefined;
  const iosPadBodyFontSize = isIOSPad && isMobileLandscape ? '16px' : undefined;
  const iosPadSmallFontSize = isIOSPad && isMobileLandscape ? '14px' : undefined;
  
  const androidTextStyle = isAndroidTablet ? (
    <style>{`
      .android-tablet-text-contact, .android-tablet-text-contact * {
        font-size: ${androidBodyFontSize};
        line-height: ${androidBodyLineHeight};
      }
    `}</style>
  ) : null;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const iosTopPad = isIOSApp ? (isIOSLarge ? 72 : 40) : 0;
  const iosBottomPad = isIOSApp ? (isIOSLarge ? 115 : 70) : 0;
  const backgroundSrc = (isIOSPad || isIPhone) ? "TabletBackground.png" : LEADERBOARD_BG;
  // iPhone-specific adjustments: move right and make narrower (matching Leaderboard)
  const iphoneRightShift = isIPhone ? 10 : 0; // Move right by 10px (keeps left side in place)
  const iphoneRightNarrow = isIPhone ? 27 : 0; // Add 27px to right to make narrower

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, subject, message }),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to send message");
      }

      setSent(true);
      // Reset form
      setName("");
      setEmail("");
      setSubject("");
      setMessage("");
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSent(false);
        navigate("/");
      }, 5000);
    } catch (err: any) {
      setError(err.message || "Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <PageLayout account={account} onLogout={onLogout}>
      {(isAndroid || isIOSApp || isIPhone) && (
        <div
          className="fixed inset-0 w-screen h-screen overflow-hidden"
          style={{ zIndex: 0, pointerEvents: "none" }}
          aria-hidden="true"
        >
          <img
            src={getAssetPath(backgroundSrc)}
            alt=""
            className="absolute inset-0 w-full h-full"
            style={{ objectFit: "cover", objectPosition: "center" }}
            draggable={false}
          />
        </div>
      )}
      {androidTextStyle}
      <div 
        className={`${isMobileLandscape ? "fixed z-40" : "px-2 md:px-6 pb-4"} ${isAndroidTablet ? "android-tablet-text-contact" : ""}`}
        style={isMobileLandscape ? (
          isIOSPad ? {
            top: `calc(env(safe-area-inset-top, 0px) + ${iosTopPad}px + ${iosPadGutterY / 2 + iosPadShiftY}px)`,
            left: `calc(env(safe-area-inset-left, 0px) + 32px + 112px + ${iosPadGutterX}px)`,
            right: `calc(env(safe-area-inset-right, 0px) + 48px + ${iosPadGutterX}px)`,
            bottom: scrollReduction
              ? `calc(env(safe-area-inset-bottom, 0px) + ${scrollReduction}px + 56px + ${iosBottomPad}px + ${iosPadGutterY / 2}px)`
              : `calc(env(safe-area-inset-bottom, 0px) + 56px + ${iosBottomPad}px + ${iosPadGutterY / 2}px)`,
            overflowY: 'auto',
            overflowX: 'auto',
            display: 'flex',
            flexDirection: 'column',
            marginLeft: '-20px',
            marginRight: 0,
            padding: '0.5rem',
            fontSize: textScale > 1 ? `${textScale}em` : undefined,
            lineHeight: textScale > 1 ? 1.2 : undefined,
            transform: (androidContentShift || iosPadShiftX) ? `translateX(${(androidContentShift || 0) + iosPadShiftX}px)` : undefined,
            ...(androidBodyFontSize ? { fontSize: androidBodyFontSize, lineHeight: androidBodyLineHeight } : {}),
          } : {
            top: isAndroidTablet
              ? `calc(32px + ${androidMenuOffset}px)`
              : (isAndroid ? '32px' : (isIPhone ? '32px' : 'env(safe-area-inset-top, 0px)')),
            left: isAndroid
              ? `calc(env(safe-area-inset-left, 0px) + 32px + 112px + ${androidInsetLeft}px)`
              : isIPhone
              ? `calc(env(safe-area-inset-left, 0px) + 32px + 112px + ${iphoneRightShift}px)`
              : 'calc(env(safe-area-inset-left, 0px) + 32px + 112px)',
            right: isAndroid
              ? `calc(env(safe-area-inset-right, 0px) + 48px + ${androidInsetRight}px)`
              : isIPhone
              ? `calc(env(safe-area-inset-right, 0px) + 48px + ${iphoneRightNarrow}px)`
              : 'calc(env(safe-area-inset-right, 0px) + 48px)',
            bottom: scrollReduction
              ? (isIPhone ? `calc(env(safe-area-inset-bottom, 8px) - 14px + 56px + ${scrollReduction}px)` : `calc(env(safe-area-inset-bottom, 0px) + ${scrollReduction}px + 56px)`)
              : (isIPhone ? 'calc(env(safe-area-inset-bottom, 8px) - 14px + 56px)' : 'calc(env(safe-area-inset-bottom, 0px) + 56px)'), // iPhone matches Leaderboard, others allow space for Send button height
            overflowY: 'auto',
            overflowX: 'auto',
            display: 'flex',
            flexDirection: 'column',
            marginLeft: '-20px',
            marginRight: 0,
            padding: '0.5rem',
            fontSize: textScale > 1 ? `${textScale}em` : undefined,
            lineHeight: textScale > 1 ? 1.2 : undefined,
            transform: androidContentShift ? `translateX(${androidContentShift}px)` : undefined,
            ...(androidBodyFontSize ? { fontSize: androidBodyFontSize, lineHeight: androidBodyLineHeight } : {}),
          }
        ) : (isAndroid ? { paddingLeft: `${androidInsetLeft}px`, paddingRight: `${androidInsetRight}px`, fontSize: textScale > 1 ? `${textScale}em` : undefined, lineHeight: textScale > 1 ? 1.2 : undefined } : (textScale > 1 ? { fontSize: `${textScale}em`, lineHeight: 1.2 } : {}))}
      >
        <div style={isAndroidTablet ? { marginLeft: '12px' } : (isIOSPad && isMobileLandscape ? { 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'flex-start',
          paddingTop: '8rem',
          marginTop: '-4rem'
        } : undefined)}>
        <Card title="Contact Us" isMobileLandscape={isMobileLandscape} iosPadHeaderFontSize={iosPadHeaderFontSize} iosPadSmallFontSize={iosPadSmallFontSize}>
          {sent ? (
            <div className="text-center py-4">
              <p className={isMobileLandscape ? "text-sm font-semibold mb-1.5" : "text-lg font-semibold mb-2"} style={{ 
                color: '#3e2615',
                fontSize: iosPadBodyFontSize
              }}>
                Thank you for your message!
              </p>
              <p className={isMobileLandscape ? "text-[10px]" : ""} style={{ 
                color: '#3e2615', 
                opacity: 0.8,
                fontSize: iosPadSmallFontSize
              }}>
                We'll get back to you as soon as possible.
              </p>
            </div>
          ) : (
            <form className={isMobileLandscape ? "space-y-1" : "space-y-4"} onSubmit={handleSubmit}>
              <div className={isMobileLandscape ? "flex gap-1 px-1" : ""}>
                <div className={isMobileLandscape ? "flex-1" : ""}>
                  <label className={isMobileLandscape ? "block text-[10px] font-medium mb-0.5" : "block text-sm font-medium mb-1"} style={{ 
                    color: '#3e2615',
                    fontSize: iosPadSmallFontSize
                  }}>
                    Name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className={isMobileLandscape ? "w-full rounded px-2 py-1 text-[11px] outline-none transition-all" : "w-full rounded-xl p-3 outline-none transition-all"}
                    style={{
                      border: '2px solid #c4a07a',
                      background: '#ffffff',
                      color: '#3e2615',
                      minHeight: isMobileLandscape ? '30px' : 'auto',
                      fontSize: isMobileLandscape ? '11px' : 'inherit'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#d4a05f'}
                    onBlur={(e) => e.target.style.borderColor = '#c4a07a'}
                  />
                </div>
                <div className={isMobileLandscape ? "flex-1" : ""}>
                  <label className={isMobileLandscape ? "block text-[10px] font-medium mb-0.5" : "block text-sm font-medium mb-1"} style={{ 
                    color: '#3e2615',
                    fontSize: iosPadSmallFontSize
                  }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className={isMobileLandscape ? "w-full rounded px-2 py-1 text-[11px] outline-none transition-all" : "w-full rounded-xl p-3 outline-none transition-all"}
                    style={{
                      border: '2px solid #c4a07a',
                      background: '#ffffff',
                      color: '#3e2615',
                      minHeight: isMobileLandscape ? '30px' : 'auto',
                      fontSize: isMobileLandscape ? '11px' : 'inherit'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#d4a05f'}
                    onBlur={(e) => e.target.style.borderColor = '#c4a07a'}
                  />
                </div>
              </div>

              <div className={isMobileLandscape ? "px-1" : ""}>
                <label className={isMobileLandscape ? "block text-[10px] font-medium mb-0.5" : "block text-sm font-medium mb-1"} style={{ 
                  color: '#3e2615',
                  fontSize: iosPadSmallFontSize
                }}>
                  Subject
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                  className={isMobileLandscape ? "w-full rounded px-2 py-1 text-[12px] outline-none transition-all" : "w-full rounded-xl p-3 outline-none transition-all"}
                  style={{
                    border: '2px solid #c4a07a',
                    background: '#ffffff',
                    color: '#3e2615',
                    minHeight: isMobileLandscape ? '32px' : 'auto',
                    fontSize: isMobileLandscape ? '12px' : 'inherit'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#d4a05f'}
                  onBlur={(e) => e.target.style.borderColor = '#c4a07a'}
                />
              </div>

              <div className={isMobileLandscape ? "px-1" : ""}>
                <label className={isMobileLandscape ? "block text-[10px] font-medium mb-0.5" : "block text-sm font-medium mb-1"} style={{ 
                  color: '#3e2615',
                  fontSize: iosPadSmallFontSize
                }}>
                  Message
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows={isMobileLandscape ? 3 : 5}
                  className={isMobileLandscape ? "w-full rounded px-2 py-2 text-[12px] outline-none transition-all resize-none" : "w-full rounded-xl p-3 outline-none transition-all resize-none"}
                  style={{
                    border: '2px solid #c4a07a',
                    background: '#ffffff',
                    color: '#3e2615',
                    fontSize: isMobileLandscape ? '12px' : 'inherit',
                    minHeight: isMobileLandscape ? '90px' : '140px'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#d4a05f'}
                  onBlur={(e) => e.target.style.borderColor = '#c4a07a'}
                />
              </div>

              {error && (
                <div className={isMobileLandscape ? "p-1.5 rounded bg-red-100 border border-red-400 text-red-700 text-[10px]" : "p-3 rounded-lg bg-red-100 border border-red-400 text-red-700 text-sm"} style={{
                  fontSize: iosPadSmallFontSize
                }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={sending}
                className={isMobileLandscape ? "w-full py-1.25 text-xs rounded font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed" : "w-full py-3 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"}
                style={{
                  background: '#fffaeb',
                  color: '#3e2615',
                  boxShadow: '0 4px 0 #8b6542, 0 6px 12px rgba(0,0,0,0.3)',
                  border: '2px solid #8b6542',
                  minHeight: isMobileLandscape ? '34px' : 'auto',
                  fontSize: iosPadBodyFontSize
                }}
              >
                {sending ? "Sending..." : "Send Message"}
              </button>
            </form>
          )}
        </Card>
        </div>
      </div>
    </PageLayout>
  );
}
